import React, { useCallback, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import PageHeader from '../../components/shell/PageHeader';
import ListScreen from '../../components/list/ListScreen';
import useListState from '../../components/list/useListState';
import UserCell from '../../components/ui/UserCell';
import StatusPill from '../../components/ui/StatusPill';
import Button from '../../components/ui/Button';
import IconButton from '../../components/ui/IconButton';
import Modal from '../../components/overlays/Modal';
import { Field, Textarea } from '../../components/ui/Field';
import { useOverlays } from '../../components/overlays/OverlayProvider';
import { getReportCounts, listReports } from '../../actions/listActions';
import { approveReport, rejectReport } from '../../actions/reportAction';
import { formatDateTime, phone } from '../../helpers/format';
import { statusLabel } from '../../design/tokens';
import './operations.css';

const VIEWS = [
  { key: 'all', label: 'All' },
  { key: 'in_progress', label: 'In progress', attention: true },
  { key: 'delivered', label: 'Delivered' },
  { key: 'pending_or_failed', label: 'Pending / failed' },
  { key: 'cancelled', label: 'Cancelled' },
];

export default function Reports() {
  const dispatch = useDispatch();
  const { confirm, toast } = useOverlays();
  const [preview, setPreview] = useState(null);
  const [rejecting, setRejecting] = useState(null);
  const [reason, setReason] = useState('');

  const list = useListState({
    views: VIEWS,
    fetcher: listReports,
    countsFetcher: getReportCounts,
    initialSort: { column: 'created_at', direction: 'desc' },
  });

  const approve = useCallback(
    async (report) => {
      const ok = await confirm({
        title: 'Approve this report?',
        body: `${report.client_name}'s ${statusLabel(report.report_type)} will be marked approved and dispatched to them.`,
        confirmLabel: 'Approve',
      });
      if (!ok) return;
      try {
        await approveReport(dispatch, report.id);
        toast('Report approved', { tone: 'success' });
        setPreview(null);
        list.reload();
        list.reloadCounts();
      } catch {
        toast('Could not approve the report', { tone: 'error' });
      }
    },
    [confirm, dispatch, toast, list]
  );

  const submitRejection = useCallback(async () => {
    try {
      await rejectReport(dispatch, rejecting.id, { rejection_msg: reason });
      setRejecting(null);
      setReason('');
      setPreview(null);
      list.reload();
      list.reloadCounts();
    } catch {
      toast('Could not reject the report', { tone: 'error' });
    }
  }, [dispatch, rejecting, reason, toast, list]);

  const columns = useMemo(
    () => [
      {
        key: 'client',
        header: 'Client',
        width: 230,
        sortable: true,
        render: (r) => <UserCell name={r.client_name || '—'} secondary={r.email} seed={r.id} />,
      },
      { key: 'phone', header: 'Phone', width: 150, mono: true, render: (r) => phone(r.phone_number) },
      { key: 'gender', header: 'Gender', width: 100, muted: true, render: (r) => r.gender || '—' },
      {
        key: 'report_type',
        header: 'Report type',
        width: 210,
        sortable: true,
        render: (r) =>
          `${statusLabel(r.report_type)}${r.variant ? ` (${statusLabel(r.variant)})` : ''}`,
      },
      {
        key: 'status',
        header: 'Status',
        width: 130,
        sortable: true,
        render: (r) => <StatusPill status={r.status} />,
      },
      {
        key: 'created_at',
        header: 'Created at',
        width: 170,
        muted: true,
        sortable: true,
        render: (r) => formatDateTime(r.created_at),
      },
      {
        key: 'pdf',
        header: '',
        width: 60,
        render: (r) =>
          r.report_url ? (
            <IconButton
              icon="bx-file"
              size="sm"
              label="Preview report"
              onClick={(e) => {
                e.stopPropagation();
                setPreview(r);
              }}
            />
          ) : null,
      },
    ],
    []
  );

  return (
    <>
      <PageHeader
        section="Operations"
        title="Reports"
        subtitle="Generated astrology reports awaiting review or already delivered."
      />

      <ListScreen
        list={list}
        columns={columns}
        rowKey={(r) => r.id}
        noun="reports"
        selectable={false}
        searchPlaceholder="Client, email or phone"
        onRowClick={(r) => r.report_url && setPreview(r)}
        empty={{ icon: 'bx-file', title: 'No reports', description: 'Nothing matches this view.' }}
      />

      {preview && (
        <div className="sv-screen-body" style={{ paddingTop: 0 }}>
          <div className="sv-card sv-pdf-panel">
            <header className="sv-pdf-panel__head">
              <div>
                <div className="sv-card__title">{preview.client_name}</div>
                <div className="sv-pdf-panel__meta">{statusLabel(preview.report_type)}</div>
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                <Button variant="primary" onClick={() => approve(preview)}>
                  Approve
                </Button>
                <Button variant="danger-text" onClick={() => setRejecting(preview)}>
                  Reject
                </Button>
                <IconButton icon="bx-x" label="Close preview" onClick={() => setPreview(null)} />
              </div>
            </header>
            {/* An <object> renders the PDF with the browser's own viewer, which avoids
                pulling a worker bundle from a CDN the way the previous screen did. */}
            <object data={preview.report_url} type="application/pdf" className="sv-pdf-panel__frame">
              <p>
                Preview unavailable.{' '}
                <a href={preview.report_url} target="_blank" rel="noopener noreferrer">
                  Open the PDF
                </a>
              </p>
            </object>
          </div>
        </div>
      )}

      <Modal
        open={!!rejecting}
        onClose={() => setRejecting(null)}
        title="Reject report"
        size="form"
        confirmLabel="Reject report"
        confirmDisabled={!reason.trim()}
        destructive
        onConfirm={submitRejection}
      >
        <p style={{ marginTop: 0 }}>
          {rejecting?.client_name}&rsquo;s report will be sent back for regeneration. The reason is
          recorded against the order.
        </p>
        <Field label="Reason" required>
          <Textarea rows={4} value={reason} onChange={(e) => setReason(e.target.value)} />
        </Field>
      </Modal>
    </>
  );
}
